import { AxiosResponse } from 'axios'
import React, { useState } from 'react'
import { apiCall, ApiConfig } from '../../../config/api'
import { DataGridElements } from '../../../interfaces'

/*
Copyright 2022-05-09  por Alfonso Moya Ecomsur Holding S.A ©
Detalle: Se crea hook para modularizar y manejar estado interno de DeclareError.

Copyright 2022-05-12  por Alfonso Moya Ecomsur Holding S.A ©
Detalle: Se mejora uso de acciones sobre estado global creando archivos propios para
acciones de notifcación y declare error modal
*/

interface SkuOutOfScopeError {
  sku: string
  name: string
  quantity: number
  asnId: number
  skuId: number
  comment: string
}

const initialState: SkuOutOfScopeError = {
  sku: '',
  name: '',
  quantity: 0,
  asnId: 0,
  skuId: 0,
  comment: ''
}

interface StockKeepingUnitNameDto {
  name: string
  sku: string
  stockKeepingUnitId: number
}

interface InventoryIncidentInfoDto {
  inventoryIncidentId: number
  stockKeepingUnitId: number
  userId: number
  pickingRouteId: number | null
  positionId: number | null
  typeInventoryIncidentId: number
  incidentDate: string
  incidentQuantity: number
  workOrderId: number | null
  stockKeepingUnit: StockKeepingUnitNameDto
  flowId: number
}

interface InventoryIncidentCreateDto {
  stockKeepingUnitId: number
  userId: number
  pickingRouteId: number | null
  positionId: number | null
  typeInventoryIncidentId: number
  incidentQuantity: number
  workOrderId: number
  flowId: number
}

export const useDeclareError = (
  userId: number,
  closeDeclareErrorModal: () => void,
  successFinishingDeclareErrors: () => void,
  errorFinishingDeclareErrors: () => void,
  errorGettingBeforeDeclaredErrors: () => void
) => {
  const [skuOrName, setSkuOrName] = useState(false)

  const [formError, setFormError] = useState<SkuOutOfScopeError>(initialState)

  const [newErrors, setNewErrors] = useState<SkuOutOfScopeError[]>([])

  const [declaredErrors, setDeclaredErrors] = useState<SkuOutOfScopeError[]>([])

  const [isLoading, setLoading] = useState<boolean>(false)

  const mapFromInventoryIncidentToTable = (
    elements: InventoryIncidentInfoDto[]
  ) => {
    const mappedFromDto: SkuOutOfScopeError[] = elements.map((item) => ({
      sku: item.stockKeepingUnit.sku,
      name: item.stockKeepingUnit.name,
      quantity: item.incidentQuantity,
      asnId: item.workOrderId || 0,
      skuId: item.stockKeepingUnit.stockKeepingUnitId,
      comment: ''
    }))

    return mappedFromDto
  }

  const mapToInventoryIncident = (elements: SkuOutOfScopeError[]) => {
    const mappedToDto: InventoryIncidentCreateDto[] = elements.map((item) => ({
      typeInventoryIncidentId: 5,
      workOrderId: item.asnId,
      userId,
      stockKeepingUnitId: item.skuId,
      incidentQuantity: item.quantity,
      flowId: 4,
      pickingRouteId: null,
      positionId: null
    }))

    return mappedToDto
  }

  const getDeclaredErrors = async (asnID: number) => {
    apiCall(
      'GET',
      ApiConfig.AsnReturnApi +
        `/${asnID}/UnexpectedProducts?pageIndex=0&pageSize=100`
    )
      .then(({ data }: AxiosResponse) => {
        setDeclaredErrors(data.items)
      })
      .catch(errorGettingBeforeDeclaredErrors)
  }

  const getDeclaredErrorsWithInventoryIncident = async (
    WorkOrderId: number
  ) => {
    apiCall('GET', ApiConfig.InventoryIncident + `?WorkOrderId=${WorkOrderId}`)
      .then(
        ({
          data
        }: AxiosResponse<DataGridElements<InventoryIncidentInfoDto>>) => {
          const mappedFromResponseSkusOutOfScope =
            mapFromInventoryIncidentToTable(data.items)

          setDeclaredErrors(mappedFromResponseSkusOutOfScope)
        }
      )
      .catch(errorGettingBeforeDeclaredErrors)
  }

  const shouldAddErrorToList = () =>
    formError.sku === '' || formError.name === '' || formError.quantity === 0

  const addErrorToList = (e: React.FormEvent, commentParam?: string) => {
    e.preventDefault()
    if (shouldAddErrorToList()) return

    const existSKU = newErrors.filter(
      (declaredError) => declaredError.sku === formError.sku
    )[0]

    let elementsToAddAsSkuErrors: SkuOutOfScopeError[] = []

    if (existSKU) {
      const calculateSkuSUM =
        parseInt(existSKU.quantity.toString()) +
        parseInt(formError.quantity.toString())
      const newElement: SkuOutOfScopeError = {
        ...existSKU,
        quantity: +calculateSkuSUM,
        skuId: existSKU.skuId,
        comment: commentParam || '' // Agregar el comentario recibido
      }

      const filteredErrors = newErrors.filter(
        (declaredError) => declaredError.sku !== formError.sku
      )

      elementsToAddAsSkuErrors = [...filteredErrors, newElement]
    } else {
      const newElement: SkuOutOfScopeError = {
        ...formError,
        quantity: +formError.quantity,
        skuId: formError.skuId,
        comment: commentParam || '' // Agregar el comentario recibido
      }
      elementsToAddAsSkuErrors = [...newErrors, newElement]
    }

    addSkuErrorToList(elementsToAddAsSkuErrors)
    setFormError(initialState)
  }

  const deleteErrorFromList = (sku: string) => {
    const filterRegisteredError = newErrors.filter((error) => sku !== error.sku)
    setNewErrors(filterRegisteredError)
  }

  const addSkuErrorToList = (skuOutOfScopeErrors: SkuOutOfScopeError[]) => {
    setNewErrors(skuOutOfScopeErrors)
  }

  const handleFinishDeclareErrorWithInventoryIncident = (asnID: number) => {
    setLoading(true)
    apiCall(
      'POST',
      `${ApiConfig.InventoryIncident}/PostList`,
      mapToInventoryIncident(newErrors)
    )
      .then(() => {
        getDeclaredErrors(asnID)
        setNewErrors([])
      })
      .then(successFinishingDeclareErrors)
      .catch(errorFinishingDeclareErrors)
      .then(() => setLoading(false))
      .finally(closeDeclareErrorModal)
  }

  const handleFinishDeclareError = (asnID: number) => {
    setLoading(true)

    // Los errores en newErrors ya tienen sus comentarios individuales
    apiCall(
      'POST',
      `${ApiConfig.AsnReturnApi}/${asnID}/UnexpectedProducts`,
      newErrors
    )
      .then(() => {
        getDeclaredErrors(asnID)
        setNewErrors([])
      })
      .then(successFinishingDeclareErrors)
      .catch(errorFinishingDeclareErrors)
      .then(() => setLoading(false))
      .finally(closeDeclareErrorModal)
  }

  return {
    skuOrName,
    setSkuOrName,
    formError,
    setFormError,
    newErrors,
    setNewErrors,
    declaredErrors,
    setDeclaredErrors,
    getDeclaredErrors,
    addSkuErrorToList,
    deleteErrorFromList,
    addErrorToList,
    handleFinishDeclareError,
    isLoading,
    setLoading,
    handleFinishDeclareErrorWithInventoryIncident,
    getDeclaredErrorsWithInventoryIncident
  }
}
